import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  // octa-tap dá scale 0.97 no :active (sensação haptic). Easing premium em todas as variantes.
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Default: gradient cor primária do tenant + multi-layer shadow tinted brand (protagonista)
        default: 'bg-octa-gradient text-white shadow-brand-cta hover-cta-lift',
        // Brand (alias semântico de default — usar pra CTAs principais)
        brand: 'bg-octa-gradient text-white shadow-brand-cta hover-cta-lift',
        // Destructive: solid red, no gradient pra não competir com primary
        destructive:
          'octa-tap bg-destructive text-destructive-foreground shadow-octa hover:bg-destructive/90',
        // Outline: borda colorida sutil + hover preenche
        outline:
          'octa-tap border border-input bg-background/60 backdrop-blur-sm shadow-octa-sm hover:bg-accent hover:text-accent-foreground hover:border-primary/40',
        // Secondary: cinza sutil
        secondary:
          'octa-tap bg-secondary text-secondary-foreground shadow-octa hover:bg-secondary/80',
        // Ghost: sem borda, sem fundo
        ghost: 'octa-tap hover:bg-accent hover:text-accent-foreground',
        // Link
        link: 'text-primary underline-offset-4 hover:underline',
        // Accent (CTA premium) — antes era gradient amber; agora é mesmo gradient brand
        // com shadow ainda mais forte pra ações destaque (compra de plano, "aceitar convite")
        accent: 'bg-octa-gradient text-white shadow-brand-cta-hero hover-cta-lift',
        // Glass: aero translucente (úteis em hero gradients)
        glass:
          'octa-tap octa-glass-premium text-foreground hover:bg-white/85 dark:hover:bg-card/85 border border-white/40 dark:border-white/10',
      },
      size: {
        default: 'h-10 px-5 py-2 text-sm',
        sm: 'h-8 rounded-lg px-3 text-xs',
        lg: 'h-12 rounded-xl px-7 text-base',
        // XL: CTAs hero (gerar código, comprar plano)
        xl: 'h-14 rounded-2xl px-8 text-base font-bold tracking-tight',
        // Hero: ações primárias gigantes em telas de transição/checkout
        hero: 'h-16 rounded-2xl px-10 text-lg font-bold tracking-tight [&_svg]:size-5',
        icon: 'h-10 w-10 rounded-xl',
        // Pill-icon — botões circulares premium
        pill: 'h-10 px-4 rounded-full text-sm',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
